import type { BenchmarkRunDetail, BenchmarkVerdict } from '@owl/shared';
import { Fragment, useEffect, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { formatInt, formatUsd } from '../components/charts/AiUsageBlocks';
import { Icon } from '../components/icons';
import { activateOnKey } from '../lib/activateOnKey';
import { ApiError, api } from '../lib/api';
import { formatDateTime } from '../lib/time';

const VERDICT_COLOR: Record<BenchmarkVerdict, string> = {
  pass: '#2e9e5b',
  partial: '#c9921f',
  fail: '#d9534f',
  na: '#888',
};

function VerdictBadge({ verdict }: { verdict: BenchmarkVerdict }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        color: '#fff',
        background: VERDICT_COLOR[verdict],
      }}
    >
      {verdict}
    </span>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div
      style={{
        background: 'var(--bg-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 10,
        padding: '12px 14px',
        minWidth: 140,
      }}
    >
      <div className="dim" style={{ fontSize: 11, marginBottom: 4 }}>
        {label}
      </div>
      <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
        {value}
      </div>
      {hint && (
        <div className="dim" style={{ fontSize: 10.5, marginTop: 2 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

export function BenchmarkDetail({ ctx, runId }: { ctx: ShellCtx; runId: string }) {
  const [run, setRun] = useState<BenchmarkRunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch on refreshTick
  useEffect(() => {
    let active = true;
    api
      .getBenchmarkRun(runId)
      .then((res) => {
        if (active) setRun(res);
      })
      .catch((err) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load');
      });
    return () => {
      active = false;
    };
  }, [runId, ctx.refreshTick]);

  const scored = run ? run.passCount + run.partialCount + run.failCount : 0;
  const passPct = scored > 0 && run ? Math.round((100 * run.passCount) / scored) : 0;

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <button
            type="button"
            className="nav-item"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}
            onClick={() => ctx.navigate({ name: 'benchmark' })}
          >
            <Icon.ChevronLeft size={13} /> Benchmarks
          </button>
          <div className="view-title">
            {run?.label || (run ? formatDateTime(run.createdAt) : 'Benchmark run')}
          </div>
          {run && (
            <div className="view-sub">
              {formatDateTime(run.createdAt)} · {run.applicationName ?? 'unknown app'} ·{' '}
              <span className="mono">{run.agentModel}</span>
              {run.mode === 'mock' && ' · mock'}
              {run.aborted && ' · aborted (budget cap)'}
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {run === null && !error ? (
        <div className="empty-state">Loading…</div>
      ) : run ? (
        <>
          {run.notes && (
            <div
              style={{
                background: 'var(--bg-2)',
                border: '1px solid var(--border-1)',
                borderLeft: '3px solid var(--accent-2, #2b5fb3)',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 16,
                whiteSpace: 'pre-wrap',
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <div
                className="dim"
                style={{
                  fontSize: 11,
                  marginBottom: 6,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                Resumen / qué atacar
              </div>
              {run.notes}
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <Stat
              label="Pass rate"
              value={`${passPct}%`}
              hint={`${run.passCount}/${scored} pass`}
            />
            <Stat
              label="P / Pa / F"
              value={`${run.passCount} / ${run.partialCount} / ${run.failCount}`}
            />
            <Stat label="Cost / conversation" value={formatUsd(run.costPerConversationUsd)} />
            <Stat
              label="Product cost (AI)"
              value={formatUsd(run.productCostUsd)}
              hint={`${run.scenarioCount} conversations`}
            />
            <Stat label="Test cost (sim + judge)" value={formatUsd(run.testCostUsd)} />
            <Stat
              label="Tokens / turn"
              value={`${formatInt(run.avgInputTokensPerTurn)} / ${formatInt(run.avgOutputTokensPerTurn)}`}
              hint="in / out"
            />
            <Stat
              label="Railway (last 30d)"
              value={run.railwayLast30dUsd === null ? '—' : formatUsd(run.railwayLast30dUsd)}
              hint="fixed infra, amortized over the month"
            />
          </div>

          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Verdict</th>
                  <th>Score</th>
                  <th>Outcome</th>
                  <th>Turns</th>
                  <th>Cost</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {run.conversations.map((cv) => {
                  const isOpen = expanded === cv.id;
                  return (
                    <Fragment key={cv.id}>
                      <tr
                        className="data-row"
                        tabIndex={0}
                        onClick={() => setExpanded(isOpen ? null : cv.id)}
                        onKeyDown={(e) =>
                          activateOnKey(e, () => setExpanded(isOpen ? null : cv.id))
                        }
                      >
                        <td>
                          <strong>{cv.scenarioName}</strong>
                        </td>
                        <td>
                          <VerdictBadge verdict={cv.verdict} />
                        </td>
                        <td className="mono">{cv.score}</td>
                        <td>
                          <span className="mono" style={{ fontSize: 12 }}>
                            {cv.outcome}
                          </span>
                          {cv.expectedOutcome && (
                            <span className="dim" style={{ fontSize: 11 }}>
                              {' '}
                              / exp. {cv.expectedOutcome}
                            </span>
                          )}
                        </td>
                        <td className="mono">{cv.turns}</td>
                        <td className="mono">{formatUsd(cv.productCostUsd)}</td>
                        <td>{cv.issues.length > 0 ? cv.issues.length : '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={{ background: 'var(--bg-2)' }}>
                            {cv.reasoning && (
                              <div style={{ marginBottom: 10 }}>
                                <strong style={{ fontSize: 12 }}>Judge:</strong>{' '}
                                <span style={{ fontSize: 12.5 }}>{cv.reasoning}</span>
                              </div>
                            )}
                            {cv.issues.length > 0 && (
                              <ul style={{ margin: '0 0 10px', paddingLeft: 18, fontSize: 12.5 }}>
                                {cv.issues.map((iss) => (
                                  <li key={iss}>{iss}</li>
                                ))}
                              </ul>
                            )}
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 6,
                                maxWidth: 720,
                              }}
                            >
                              {cv.transcript.map((turn) => {
                                const isCustomer = turn.role === 'customer';
                                return (
                                  <div
                                    key={`${turn.role}:${turn.text}`}
                                    style={{
                                      alignSelf: isCustomer ? 'flex-start' : 'flex-end',
                                      maxWidth: '85%',
                                      background: isCustomer
                                        ? 'var(--bg-1)'
                                        : 'var(--accent-2, #2b5fb3)',
                                      color: isCustomer ? 'inherit' : '#fff',
                                      border: isCustomer ? '1px solid var(--border-1)' : 'none',
                                      borderRadius: 10,
                                      padding: '6px 10px',
                                      fontSize: 12.5,
                                      whiteSpace: 'pre-wrap',
                                    }}
                                  >
                                    <div
                                      className="dim"
                                      style={{
                                        fontSize: 10,
                                        marginBottom: 2,
                                        color: isCustomer ? 'var(--fg-2)' : 'rgba(255,255,255,0.8)',
                                      }}
                                    >
                                      {isCustomer ? 'Customer' : 'VERA'}
                                    </div>
                                    {turn.text}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
